const SELECTOR = '[data-product-media-mobile-loop]';

class ProductMediaMobileLoop {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-loop-track]');
    this.thumb = root.querySelector('[data-loop-thumb]');
    this.scrollbarTrack = root.querySelector('.product-media-mobile-loop__scrollbar .slideshow-scrollbar__track');
    this.count = Number(root.dataset.loopCount || 0);
    this.scrollTimeout = 0;
    this.currentIndex = 1;
    this.raf = 0;
    this.isResetting = false;
    this.entranceAnimation = null;

    if (!this.track || this.count < 2) return;

    this.init();
  }

  init() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.jumpTo(1);
        this.updateScrollbar();
      });
    });

    this.track.addEventListener('scroll', () => {
      if (!this.isResetting) this.requestScrollbarUpdate();
      window.clearTimeout(this.scrollTimeout);
      this.scrollTimeout = window.setTimeout(() => this.handleScrollEnd(), 120);
    }, { passive: true });

    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', () => this.handleScrollEnd(), { passive: true });
    }

    window.addEventListener('resize', () => {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        this.jumpTo(this.currentIndex);
        this.updateScrollbar();
      }, 150);
    });
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return Array.from(this.track.querySelectorAll('[data-loop-slide]'));
  }

  /** @param {number} index */
  getSlide(index) {
    return this.slides[index];
  }

  nearestIndex() {
    const slides = this.slides;
    if (!slides.length) return 1;

    const scrollLeft = this.track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  /** @param {number} index */
  jumpTo(index) {
    const slide = this.getSlide(index);
    if (!slide) return;

    const previousBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    this.track.style.scrollBehavior = previousBehavior;
  }

  requestScrollbarUpdate() {
    if (this.raf) return;

    this.raf = window.requestAnimationFrame(() => {
      this.raf = 0;
      this.updateScrollbar();
    });
  }

  updateScrollbar() {
    if (!this.thumb) return;
    this.entranceAnimation?.cancel();
    this.entranceAnimation = null;

    const metrics = this.scrollbarMetrics();
    if (!metrics) return;

    const { thumbWidth, maxX, progress } = metrics;

    this.thumb.style.transition = 'none';
    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translate3d(${maxX * progress}px, 0, 0)`;
  }

  scrollbarMetrics() {
    const firstRealSlide = this.getSlide(1);
    const lastRealSlide = this.getSlide(this.count);
    if (!firstRealSlide || !lastRealSlide) return null;

    const scrollbarWidth = this.scrollbarTrack?.clientWidth || this.track.clientWidth;
    const thumbWidth = Math.max(30, scrollbarWidth / this.count);
    const maxX = Math.max(0, scrollbarWidth - thumbWidth);
    const span = Math.max(1, lastRealSlide.offsetLeft - firstRealSlide.offsetLeft);
    const rawProgress = (this.track.scrollLeft - firstRealSlide.offsetLeft) / span;
    const minProgress = maxX > 0 ? -thumbWidth / maxX : 0;
    const maxProgress = maxX > 0 ? scrollbarWidth / maxX : 1;
    const progress = Math.min(maxProgress, Math.max(minProgress, rawProgress));

    return { scrollbarWidth, thumbWidth, maxX, progress };
  }

  /** @param {'start' | 'end'} edge */
  animateThumbIntoView(edge) {
    if (!this.thumb) return;

    const metrics = this.scrollbarMetrics();
    if (!metrics) return;

    const { scrollbarWidth, thumbWidth, maxX } = metrics;
    const fromX = edge === 'start' ? -thumbWidth : scrollbarWidth;
    const toX = edge === 'start' ? 0 : maxX;
    const fromTransform = `translate3d(${fromX}px, 0, 0)`;
    const toTransform = `translate3d(${toX}px, 0, 0)`;

    this.entranceAnimation?.cancel();
    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transition = 'none';
    this.thumb.style.transform = fromTransform;

    this.entranceAnimation = this.thumb.animate(
      [{ transform: fromTransform }, { transform: toTransform }],
      {
        duration: 180,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      }
    );

    this.entranceAnimation.onfinish = () => {
      this.thumb.style.transform = toTransform;
      this.entranceAnimation?.cancel();
      this.entranceAnimation = null;
      this.isResetting = false;
    };
  }

  handleScrollEnd() {
    if (this.isResetting) return;

    const index = this.nearestIndex();

    if (index === 0) {
      this.isResetting = true;
      this.jumpTo(this.count);
      this.currentIndex = this.count;
      this.animateThumbIntoView('end');
      return;
    }

    if (index === this.count + 1) {
      this.isResetting = true;
      this.jumpTo(1);
      this.currentIndex = 1;
      this.animateThumbIntoView('start');
      return;
    }

    this.currentIndex = index;
    this.updateScrollbar();
  }
}

function init() {
  document.querySelectorAll(SELECTOR).forEach((root) => {
    if (root instanceof HTMLElement && !root.dataset.mobileLoopInitialized) {
      root.dataset.mobileLoopInitialized = 'true';
      new ProductMediaMobileLoop(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

new MutationObserver(init).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
